import { useState } from "react";
import { sendSlackMessageTracked } from "../../../../models/notifications";
import GeneralHeroSection from "../general/generalHeroSection";
import QuoteBlock from "../../../general/ui-blocks/elements/quoteBlock";
import NoImageSectionCard from "../../../general/noImageSectionCard";
import MiniSection from "../general/miniSection";
import Button from "../../../general/ui-blocks/elements/button";
import GeneralFAQs from "../general/generalFAQs";
import HighlightedCardGroup from "../../../general/highlightedCardGroup";


function CurriculumRequest() {
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email) return;
        sendSlackMessageTracked(`📋 Advanced Curriculum Request — Founder Track\nEmail: ${email}`);
        sendSlackMessageTracked(`🚨 Curriculum Request — Founder Track\nEmail: ${email}`, "#high-alerts");
        setSubmitted(true);
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
            <p className="font-semibold mb-1" style={{ fontSize: "clamp(1rem, 2vw, 1.1rem)" }}>
                Request the Advanced Curriculum
            </p>
            <p className="text-gray-500 text-sm mb-4">
                Get the full week-by-week breakdown sent to your inbox.
            </p>
            {submitted ? (
                <p className="text-sm font-medium" style={{ color: "#D23C1E" }}>
                    Got it — we'll send it your way shortly.
                </p>
            ) : (
                <form onSubmit={handleSubmit} className="flex flex-wrap gap-2">
                    <input
                        type="email"
                        required
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="border border-gray-300 rounded-md px-4 py-2 text-sm flex-1 min-w-0 focus:outline-none focus:ring-2"
                        style={{ minWidth: "200px", focusRingColor: "#D23C1E" }}
                    />
                    <button
                        type="submit"
                        className="px-5 py-2 rounded-md text-sm font-semibold text-white"
                        style={{ backgroundColor: "#D23C1E" }}
                    >
                        Send It
                    </button>
                </form>
            )}
        </div>
    );
}

export default function SecondaryIntensiveBody() {

    return (
        <div>
            <main role="main" className="flex-grow-1">
                <a id="main-content" tabIndex="-1"></a>

                <div className="row g-0">
                    <div className="col-12 col-lg">
                        <article>
                            <div className="full-story">

                                <GeneralHeroSection
                                    title="Mastery | Founder Track · February 2027"
                                    imageURL="https://storage.googleapis.com/extern-academy.firebasestorage.app/images/1114891.webp"
                                    imageALT="Mastery | Founder Track · February 2027"
                                    imageSubtitle="Application Deadline: January 31, 2027"
                                    blurb={
                                        <div>
                                            {/* Badge strip */}
                                            <div className="flex flex-wrap gap-2 mb-10">
                                                <span
                                                    className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-semibold text-white"
                                                    style={{ backgroundColor: "#3769B9" }}
                                                >
                                                    Grades 11 &amp; 12
                                                </span>
                                                <span
                                                    className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-semibold text-white"
                                                    style={{ backgroundColor: "#3769B9" }}
                                                >
                                                    February 2027 · 28 Days
                                                </span>
                                                <span
                                                    className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-semibold text-white"
                                                    style={{ backgroundColor: "#D23C1E" }}
                                                >
                                                    30 Seats
                                                </span>
                                            </div>

                                            <h2 className="font-bold mb-4" style={{ fontSize: "clamp(1.5rem, 4vw, 2rem)" }}>
                                                This is how founders are made.
                                            </h2>
                                            <p>
                                                28 days to go from idea to live product to real users. The
                                                Founder Track gives you the time and the iteration cycles to build
                                                something worth using, and see what it actually takes to run it
                                                after launch.
                                            </p>
                                        </div>
                                    }
                                />

                                {/* ── Program Overview ─────────────────────────────────── */}
                                <div className="container py-12 flex justify-center">
                                    <div className="space-y-12 w-3/4">

                                        <MiniSection
                                            title="Meeting Structure & Scheduling"
                                            sections={[
                                                <p>
                                                    <strong>Standups: </strong>You'll meet with your team twice
                                                    a week. Everything is online, so you
                                                    can join from anywhere. These meetings include your entire team
                                                    plus your Startup Trainer.
                                                </p>,
                                                <p>
                                                    <strong>Scheduling: </strong>We know your schedule is packed.
                                                    After you apply, you'll choose from several available time slots.
                                                    Your selected time determines your team and your standing meeting
                                                    schedule.
                                                </p>,
                                                <p>
                                                    <strong>Feedback Loop: </strong>After each standup, you'll
                                                    receive a short survey to share how productive the meeting felt.
                                                    It's how we keep sessions on track and useful for your team.
                                                </p>,
                                            ]}
                                        />

                                        <hr className="border-gray-200" />

                                        <MiniSection
                                            title="Your Role"
                                            sections={[
                                                <p>
                                                    Every extern on the Founder Track holds a C-Suite title. You're the CEO, CTO, CMO, or CPO
                                                    of your product for 28 days. The decisions are yours to make.
                                                </p>,
                                                <p>
                                                    <strong>Teams of 10: </strong>Each team is composed of 10 externs,
                                                    each owning a distinct function. You'll work alongside peers who
                                                    are just as invested in shipping something real as you are,
                                                    coached by a dedicated Startup Trainer.
                                                </p>,
                                                <p>
                                                    <strong>AI Tools & Vibe Coding: </strong>You're encouraged to
                                                    use modern AI tools and "vibe coding" to move fast. Your Startup
                                                    Trainer will show you how to leverage these tools effectively.
                                                </p>,
                                                <p>
                                                    <strong>The Rules of the Game: </strong>In your very first
                                                    meeting, your Startup Trainer will lay out the "rules of the
                                                    game." These guidelines help you navigate collaboration in a
                                                    fast‑moving environment. We know this is new for many of you, so
                                                    we'll give it the time and attention it deserves.
                                                </p>,
                                            ]}
                                        />

                                        <hr className="border-gray-200" />

                                        <MiniSection
                                            title="Your Product"
                                            sections={[
                                                <p>
                                                    <strong>Consumer Product: </strong>You'll be building a consumer
                                                    product: something people can use. This can be an iOS/Android
                                                    app or a web app. Your Startup Trainer has spent hundreds of
                                                    hours vetting and selecting the most suitable concepts, so you're
                                                    building something with actual market potential.
                                                </p>,
                                                <p>
                                                    <strong>Project vs. Product: </strong>At Extern Academy, you're
                                                    not building a project. You're building a{" "}<i
                                                    className="font-light">product</i>. A product has
                                                    users. It solves a real problem and it lives on after the
                                                    deadline.
                                                </p>,
                                                <p>
                                                    <strong>After Launch: </strong>After the product goes live,
                                                    you'll enter a cycle of iteration — gathering feedback, making
                                                    improvements, and acquiring your first real users. You'll
                                                    see what it's like to run a product after it goes live.
                                                </p>,
                                            ]}
                                        />

                                        {/* Project vs. Product comparison table */}
                                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                                            <table className="w-full border-collapse text-left">
                                                <thead>
                                                    <tr className="border-b-2 border-gray-200" style={{ backgroundColor: "#f9f9f9" }}>
                                                        <th className="py-3 px-4 font-semibold"></th>
                                                        <th className="py-3 px-4 font-semibold">School Project</th>
                                                        <th className="py-3 px-4 font-semibold" style={{ color: "#D23C1E" }}>Your Product</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="border-b border-gray-100">
                                                        <td className="py-3 px-4 font-medium">End result</td>
                                                        <td className="py-3 px-4 text-gray-500">A grade</td>
                                                        <td className="py-3 px-4 font-medium">Something people use</td>
                                                    </tr>
                                                    <tr className="border-b border-gray-100 bg-gray-50">
                                                        <td className="py-3 px-4 font-medium">Audience</td>
                                                        <td className="py-3 px-4 text-gray-500">Your teacher</td>
                                                        <td className="py-3 px-4 font-medium">Real users</td>
                                                    </tr>
                                                    <tr className="border-b border-gray-100">
                                                        <td className="py-3 px-4 font-medium">Life after</td>
                                                        <td className="py-3 px-4 text-gray-500">Ends at submission</td>
                                                        <td className="py-3 px-4 font-medium">Keeps living. You can update, share, and grow it</td>
                                                    </tr>
                                                    <tr className="bg-gray-50">
                                                        <td className="py-3 px-4 font-medium">What you gain</td>
                                                        <td className="py-3 px-4 text-gray-500">A score</td>
                                                        <td className="py-3 px-4 font-medium">A story, a portfolio piece, a launch</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        <hr className="border-gray-200" />

                                        <MiniSection
                                            title="Curriculum"
                                            sections={[
                                                <p>
                                                    <strong>Week 1: </strong>You'll go from idea to prototype. By
                                                    the end of Week 1, you'll have a working version of your product
                                                    that you can share and test with real users.
                                                </p>,
                                                <p>
                                                    <strong>Week 2: </strong>Week 2 is dedicated to polishing and
                                                    formally launching the product.
                                                </p>,
                                                <p>
                                                    <strong>Weeks 3 & 4: </strong>You'll start acquiring your first
                                                    users and iterating based on what you learn after launch.
                                                    This is where you see what it really means to run a business.
                                                </p>,
                                            ]}
                                        />

                                        <CurriculumRequest />

                                        <hr className="border-gray-200" />

                                        <MiniSection
                                            title="Admissions & Tuition"
                                            sections={[
                                                <p>
                                                    <strong>How to Apply: </strong>Submit your application form and
                                                    personal essay{" "}
                                                    <a className="brand-color underline" href="/apply" target="_blank">
                                                        here
                                                    </a>
                                                    . Once we've reviewed your materials, you'll hear back within two
                                                    weeks. Spots are limited and filled on a rolling basis, so early
                                                    applications are encouraged.
                                                </p>,
                                                <p>
                                                    <strong>Tuition: </strong>The program tuition is $2,200. This
                                                    covers everything you need to build and deploy your product,
                                                    including initial marketing efforts to help you reach your first
                                                    users.
                                                </p>,
                                            ]}
                                        />

                                        <hr className="border-gray-200" />

                                        <MiniSection
                                            title="What You Walk Away With"
                                            sections={[
                                                <p>
                                                    <strong>Your C-Suite Title: </strong>You'll have held a real
                                                    executive title — CEO, CTO, CMO, or CPO — on a live product.
                                                    That's a credential most people don't earn until their thirties.
                                                </p>,
                                                <p>
                                                    <strong>Certificate: </strong>Upon completing the program, you'll
                                                    receive a certificate of completion confirming your graduation
                                                    from Extern Academy.
                                                </p>,
                                                <p>
                                                    <strong>Recommendation Letter: </strong>You'll also receive a
                                                    letter of recommendation you can redeem when you need it, for
                                                    college applications, internships, or future job opportunities.
                                                </p>,
                                                <p>
                                                    <strong>Your Proof & The EA Guarantee: </strong>You'll leave with
                                                    something far more valuable than a grade: a real product you
                                                    built and can point to for years to come. Once deployed, we
                                                    guarantee your product stays live for one full year.
                                                </p>,
                                                <p>
                                                    <strong>Your First Professional Network: </strong>You'll stay
                                                    connected with your team and the broader cohort. The people you
                                                    build with today will be the founders, engineers, and investors
                                                    you collaborate with tomorrow.
                                                </p>,
                                            ]}
                                        />

                                    </div>
                                </div>

                                {/* ── CTA ──────────────────────────────────────────────── */}
                                <NoImageSectionCard
                                    primaryButtonRow={
                                        <Button text="START YOUR APPLICATION" ctaURL="/apply" variant="btn-danger" />
                                    }
                                />

                                {/* ── FAQ ──────────────────────────────────────────────── */}
                                <div className="container py-12">
                                    <h2 className="font-semibold mb-8" style={{ fontSize: "clamp(1.75rem, 5vw, 2.5rem)" }}>
                                        Frequently Asked Questions
                                    </h2>
                                    <div className="space-y-4">
                                        <QuoteBlock
                                            title="When does the Founder Track take place?"
                                            blurb={
                                                <p>
                                                    The program runs for <strong>28 days in February 2027.</strong>
                                                </p>
                                            }
                                        />
                                        <QuoteBlock
                                            title="What C-Suite title will I hold?"
                                            blurb={
                                                <p>
                                                    Every extern holds an executive title for the duration of the program.
                                                    Depending on your role, you'll be the <strong>CEO, CTO, CMO, or CPO</strong> of
                                                    your product. Your title is assigned based on your skills and interests
                                                    during the application process.
                                                </p>
                                            }
                                        />
                                        <QuoteBlock
                                            title="What tools will I be working with?"
                                            blurb={
                                                <div className="space-y-1">
                                                    <p>You'll use the same tools real startup teams rely on. Depending on your role:</p>
                                                    <ul className="list-disc list-inside mt-2 space-y-1">
                                                        <li><strong>Design:</strong> Figma, Canva, Adobe Creative Suite</li>
                                                        <li><strong>Development:</strong> VS Code, Git & GitHub, React, Node.js, Python 3, Next.js</li>
                                                        <li><strong>Marketing:</strong> Notion, HubSpot, Google Analytics, social media platforms</li>
                                                        <li><strong>Project management:</strong> Slack, Notion, Trello, Asana</li>
                                                    </ul>
                                                    <p className="mt-2">Your team will decide which tools best fit your product. We'll provide guidance and access to everything you need.</p>
                                                </div>
                                            }
                                        />
                                        <QuoteBlock
                                            title="How many students will be accepted?"
                                            blurb={
                                                <p>
                                                    The February cohort is capped at <strong>30 students</strong>, organized
                                                    into teams of 10, each coached by a dedicated Startup Trainer.
                                                </p>
                                            }
                                        />
                                        <QuoteBlock
                                            title="What exactly will I be working on?"
                                            blurb={
                                                <p>
                                                    You'll join a team of 10 externs and build a real consumer product
                                                    from scratch. Each member holds a C-Suite title aligned with their
                                                    skills: CEO, CTO, CMO, or CPO. Together, you'll go from idea to
                                                    live product, then spend the final two weeks acquiring real users
                                                    and iterating based on feedback.
                                                </p>
                                            }
                                        />
                                        <QuoteBlock
                                            title="How difficult is the program?"
                                            blurb={
                                                <p>
                                                    There are no tests or quizzes. This program is fast‑paced, and
                                                    the real challenge comes from solving real problems and making
                                                    decisions with your team. Expect to be challenged and stretched,
                                                    and when you need support, it's there.
                                                </p>
                                            }
                                        />
                                        <QuoteBlock
                                            title="What is the admissions process?"
                                            blurb={
                                                <p>
                                                    You can find full details on our{" "}
                                                    <a className="underline brand-color" href="/admissions" target="_blank">
                                                        Admissions page
                                                    </a>
                                                    . In short:{" "}
                                                    <a className="underline brand-color" href="/apply" target="_blank">
                                                        submit an application
                                                    </a>
                                                    , complete a short interview, and receive a decision within two
                                                    weeks. Spots are limited and filled on a rolling basis. The final
                                                    deadline to apply is{" "}
                                                    <strong>
                                                        January 31<sup>st</sup>, 2027 at 11:59 PM PST
                                                    </strong>
                                                    .
                                                </p>
                                            }
                                        />
                                        <QuoteBlock
                                            title="What is the tuition?"
                                            blurb={
                                                <p>
                                                    Tuition for the Founder Track is $2,200. There is no fee to apply.
                                                </p>
                                            }
                                        />
                                        <QuoteBlock
                                            title="Is financial aid available?"
                                            blurb={
                                                <p>
                                                    Need‑based financial aid is available for students who demonstrate
                                                    strong potential and financial need. Aid is awarded on a rolling
                                                    basis while funds last. You'll have the opportunity to indicate
                                                    your interest during the application process.
                                                </p>
                                            }
                                        />
                                    </div>
                                </div>

                                <NoImageSectionCard
                                    primaryButtonRow={
                                        <Button text="START YOUR APPLICATION" ctaURL="/apply" variant="btn-danger" />
                                    }
                                />

                                <GeneralFAQs />

                                <NoImageSectionCard
                                    primaryButtonRow={
                                        <Button text="START YOUR APPLICATION" ctaURL="/apply" variant="btn-danger" />
                                    }
                                />

                                <div className="container mb-24">
                                    <HighlightedCardGroup />
                                </div>

                            </div>
                        </article>
                    </div>
                </div>
            </main>
        </div>
    )
}
